import React, { useState } from 'react';

// US Citizenship Step 4: Travel History Analysis
export const CitizenshipStep4TravelHistory = ({ formData, setFormData, onNext, onBack }) => {
  const [travelHistory, setTravelHistory] = useState(formData.travelHistory || []);
  const [newTrip, setNewTrip] = useState({
    departureDate: '',
    returnDate: '',
    destination: '',
    purpose: '',
    duration: ''
  });

  const addTrip = () => {
    if (newTrip.departureDate && newTrip.returnDate && newTrip.destination) {
      const trip = {
        id: Date.now(),
        ...newTrip,
        duration: calculateDuration(newTrip.departureDate, newTrip.returnDate)
      };
      const updatedHistory = [...travelHistory, trip];
      setTravelHistory(updatedHistory);
      setFormData({ ...formData, travelHistory: updatedHistory });
      setNewTrip({
        departureDate: '',
        returnDate: '',
        destination: '',
        purpose: '',
        duration: ''
      });
    }
  };

  const removeTrip = (tripId) => {
    const updatedHistory = travelHistory.filter(trip => trip.id !== tripId);
    setTravelHistory(updatedHistory);
    setFormData({ ...formData, travelHistory: updatedHistory });
  };

  const calculateDuration = (departure, returnDate) => {
    const start = new Date(departure);
    const end = new Date(returnDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTotalDaysAbsent = () => {
    return travelHistory.reduce((total, trip) => total + (trip.duration || 0), 0);
  };

  const getTripsOver6Months = () => {
    return travelHistory.filter(trip => trip.duration > 180);
  };

  const getContinuousResidenceIssues = () => {
    return travelHistory.filter(trip => trip.duration > 365);
  };

  const getPhysicalPresenceCalculation = () => {
    const totalDays = getTotalDaysAbsent();
    const fiveYears = 5 * 365; // 1825 days
    const physicalPresenceDays = fiveYears - totalDays;
    const requiredDays = Math.floor(fiveYears / 2); // 912.5 days minimum
    
    return {
      totalDaysAbsent: totalDays,
      physicalPresenceDays,
      requiredDays,
      meetsRequirement: physicalPresenceDays >= requiredDays
    };
  };

  const calculation = getPhysicalPresenceCalculation();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">✈️ Travel History Analysis</h3>
        <p className="text-gray-400 text-lg">Document all trips outside the United States</p>
      </div>

      {/* Travel Requirements Overview */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">📋 Travel Requirements for Naturalization</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 className="text-blue-200 font-medium mb-2">Physical Presence Requirement:</h5>
            <ul className="text-blue-100 text-sm space-y-1">
              <li>• Must be physically present in US for at least 30 months out of 5 years</li>
              <li>• Cannot count time outside US toward this requirement</li>
            </ul>
          </div>
          <div>
            <h5 className="text-blue-200 font-medium mb-2">Continuous Residence Requirement:</h5>
            <ul className="text-blue-100 text-sm space-y-1">
              <li>• Trips over 6 months may break continuous residence</li>
              <li>• Trips over 1 year automatically break continuous residence</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Current Calculations */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📊 Current Travel Analysis</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400">{getTotalDaysAbsent()}</div>
            <div className="text-gray-300 text-sm">Total Days Absent</div>
          </div>
          <div className="text-center">
            <div className={`text-3xl font-bold ${calculation.meetsRequirement ? 'text-emerald-400' : 'text-red-400'}`}>
              {calculation.physicalPresenceDays}
            </div>
            <div className="text-gray-300 text-sm">Physical Presence Days</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-400">{getTripsOver6Months().length}</div>
            <div className="text-gray-300 text-sm">Trips Over 6 Months</div>
          </div>
          <div className="text-center">
            <div className={`text-3xl font-bold ${getContinuousResidenceIssues().length === 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {getContinuousResidenceIssues().length}
            </div>
            <div className="text-gray-300 text-sm">Residence Issues</div>
          </div>
        </div>
        
        <div className="mt-4">
          <div className={`p-3 rounded ${calculation.meetsRequirement ? 'bg-emerald-900/20 border border-emerald-400' : 'bg-red-900/20 border border-red-400'}`}>
            <p className={`text-sm ${calculation.meetsRequirement ? 'text-emerald-200' : 'text-red-200'}`}>
              <strong>Physical Presence Status:</strong> {calculation.meetsRequirement ? 
                `✅ You meet the requirement (${calculation.physicalPresenceDays}/${calculation.requiredDays} days)` :
                `❌ You may not meet the requirement (${calculation.physicalPresenceDays}/${calculation.requiredDays} days)`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Add New Trip */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">➕ Add Travel Outside US</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-300 mb-2">Departure Date</label>
            <input
              type="date"
              value={newTrip.departureDate}
              onChange={(e) => setNewTrip({ ...newTrip, departureDate: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Return Date</label>
            <input
              type="date"
              value={newTrip.returnDate}
              onChange={(e) => setNewTrip({ ...newTrip, returnDate: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Destination Country</label>
            <input
              type="text"
              value={newTrip.destination}
              onChange={(e) => setNewTrip({ ...newTrip, destination: e.target.value })}
              placeholder="e.g., Mexico, Canada, United Kingdom"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Purpose of Trip</label>
            <select
              value={newTrip.purpose}
              onChange={(e) => setNewTrip({ ...newTrip, purpose: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select purpose</option>
              <option value="vacation">Vacation/Tourism</option>
              <option value="business">Business</option>
              <option value="family">Visiting Family</option>
              <option value="medical">Medical Treatment</option>
              <option value="education">Education/Training</option>
              <option value="emergency">Family Emergency</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <button
          onClick={addTrip}
          disabled={!newTrip.departureDate || !newTrip.returnDate || !newTrip.destination}
          className={`w-full py-3 rounded-lg font-medium transition-colors ${
            newTrip.departureDate && newTrip.returnDate && newTrip.destination
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Add Trip
        </button>
      </div>

      {/* Travel History List */}
      {travelHistory.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">📋 Your Travel History</h4>
          <div className="space-y-3">
            {travelHistory.map((trip) => (
              <div key={trip.id} className={`p-4 rounded border-2 ${
                trip.duration > 365 ? 'border-red-400 bg-red-900/20' :
                trip.duration > 180 ? 'border-yellow-400 bg-yellow-900/20' :
                'border-gray-600 bg-gray-700'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-white font-medium">{trip.destination}</h5>
                      <div className="flex items-center space-x-4">
                        <span className={`text-sm px-2 py-1 rounded ${
                          trip.duration > 365 ? 'bg-red-800 text-red-200' :
                          trip.duration > 180 ? 'bg-yellow-800 text-yellow-200' :
                          'bg-gray-800 text-gray-200'
                        }`}>
                          {trip.duration} days
                        </span>
                        <button
                          onClick={() => removeTrip(trip.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-300">
                      <div>Departed: {trip.departureDate}</div>
                      <div>Returned: {trip.returnDate}</div>
                      <div>Purpose: {trip.purpose}</div>
                    </div>
                    {trip.duration > 180 && (
                      <div className={`mt-2 text-xs ${
                        trip.duration > 365 ? 'text-red-300' : 'text-yellow-300'
                      }`}>
                        ⚠️ {trip.duration > 365 ? 
                          'Trip over 1 year breaks continuous residence' : 
                          'Trip over 6 months may affect continuous residence'
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Travel Declaration */}
      {travelHistory.length === 0 && (
        <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
          <h4 className="text-emerald-300 font-semibold mb-4">✅ No International Travel</h4>
          <p className="text-emerald-200 text-sm">
            If you have not traveled outside the United States during your permanent residence, this strengthens your naturalization application by clearly meeting both physical presence and continuous residence requirements.
          </p>
        </div>
      )}

      {/* Important Warnings */}
      {(getTripsOver6Months().length > 0 || getContinuousResidenceIssues().length > 0) && (
        <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
          <h4 className="text-red-300 font-semibold mb-4">⚠️ Travel Concerns Identified</h4>
          <ul className="text-red-200 text-sm space-y-2">
            {getTripsOver6Months().length > 0 && (
              <li>• You have {getTripsOver6Months().length} trip(s) over 6 months that may affect continuous residence</li>
            )}
            {getContinuousResidenceIssues().length > 0 && (
              <li>• You have {getContinuousResidenceIssues().length} trip(s) over 1 year that break continuous residence</li>
            )}
            {!calculation.meetsRequirement && (
              <li>• You may not meet the physical presence requirement</li>
            )}
          </ul>
          <p className="text-red-200 text-sm mt-3">
            <strong>Consider:</strong> You may need to wait longer before applying or consult with an immigration attorney about your specific situation.
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Document Upload
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Background Check →
        </button>
      </div>
    </div>
  );
};

// US Citizenship Step 5: Background Check & Complication Review
export const CitizenshipStep5BackgroundReview = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.analysisResults || {});

  const performRealAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      // Real API call to analyze citizenship case
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/analyze-case`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: 'N-400 Naturalization Application',
          petitioner_details: {
            fullName: formData.backgroundDetails?.fullName || "Naturalization Applicant",
            status: formData.currentStatus || "green-card-holder",
            eligibilityPath: formData.eligibilityPath || "lawful-permanent-resident",
            yearsInUS: formData.residenceDetails?.yearsInUS || "5-plus",
            currentState: formData.residenceDetails?.currentState || "Various"
          },
          beneficiary_details: {
            fullName: formData.backgroundDetails?.fullName || "Naturalization Applicant",
            status: "citizenship-applicant",
            country: "United States",
            location: formData.residenceDetails?.currentState || "United States"
          },
          relationship_details: {
            eligibilityPath: formData.eligibilityPath || "",
            criminalHistory: formData.backgroundDetails?.criminalHistory || "none",
            taxCompliance: formData.backgroundDetails?.taxCompliance || "current",
            militaryService: formData.backgroundDetails?.militaryService || false,
            travelConcerns: getTravelConcerns(formData.travelHistory || []),
            physicalPresence: getPhysicalPresenceStatus(formData.travelHistory || []),
            continuousResidence: getContinuousResidenceStatus(formData.travelHistory || [])
          },
          documents: formData.documents || []
        })
      });

      if (response.ok) {
        const result = await response.json();
        setAnalysisResults(result);
        setFormData({ ...formData, analysisResults: result });
      } else {
        // Fallback to dynamic analysis if API fails
        const dynamicAnalysis = generateCitizenshipAnalysis(formData);
        setAnalysisResults(dynamicAnalysis);
        setFormData({ ...formData, analysisResults: dynamicAnalysis });
      }
    } catch (error) {
      console.error('Analysis error:', error);
      // Fallback to dynamic analysis
      const dynamicAnalysis = generateCitizenshipAnalysis(formData);
      setAnalysisResults(dynamicAnalysis);
      setFormData({ ...formData, analysisResults: dynamicAnalysis });
    }
    
    setAnalyzing(false);
  };

  const getTravelConcerns = (travelHistory) => {
    const trips6Months = travelHistory.filter(trip => trip.duration > 180);
    const trips1Year = travelHistory.filter(trip => trip.duration > 365);
    
    if (trips1Year.length > 0) return 'high';
    if (trips6Months.length > 0) return 'medium';
    return 'low';
  };

  const getPhysicalPresenceStatus = (travelHistory) => {
    const totalDaysAbsent = travelHistory.reduce((total, trip) => total + (trip.duration || 0), 0);
    const fiveYears = 5 * 365;
    const physicalPresenceDays = fiveYears - totalDaysAbsent;
    const requiredDays = Math.floor(fiveYears / 2);
    
    return physicalPresenceDays >= requiredDays ? 'meets' : 'fails';
  };

  const getContinuousResidenceStatus = (travelHistory) => {
    const trips1Year = travelHistory.filter(trip => trip.duration > 365);
    return trips1Year.length === 0 ? 'maintained' : 'broken';
  };

  const generateCitizenshipAnalysis = (data) => {
    const documents = data.documents || [];
    const backgroundDetails = data.backgroundDetails || {};
    const travelHistory = data.travelHistory || [];
    const eligibilityPath = data.eligibilityPath || '';
    
    // Calculate application strength using algorithms
    let applicationScore = 30; // Base score
    let eligibilityScore = 0;
    let documentScore = 0;
    let backgroundScore = 0;
    let travelScore = 0;
    let issues = [];
    let strengths = [];
    
    // Eligibility path scoring
    const pathScores = {
      'lawful-permanent-resident': 25,
      'spouse-us-citizen': 20,
      'military-service': 30,
      'derivative-citizenship': 15,
      'other-pathway': 10
    };
    eligibilityScore = pathScores[eligibilityPath] || 15;
    
    // Document analysis scoring
    const requiredDocTypes = ['identity', 'residence', 'tax', 'travel'];
    const documentTypes = documents.map(doc => doc.category);
    const foundTypes = requiredDocTypes.filter(type => 
      documentTypes.some(docType => docType.includes(type))
    );
    documentScore = (foundTypes.length / requiredDocTypes.length) * 20;
    
    // Background scoring
    const criminalHistory = backgroundDetails.criminalHistory || 'none';
    const taxCompliance = backgroundDetails.taxCompliance || 'current';
    
    if (criminalHistory === 'none') backgroundScore += 10;
    else if (criminalHistory === 'minor-traffic') backgroundScore += 8;
    else if (criminalHistory === 'misdemeanor') backgroundScore += 5;
    else if (criminalHistory === 'felony') backgroundScore += 0;
    else if (criminalHistory === 'pending') backgroundScore -= 5;
    
    if (taxCompliance === 'current') backgroundScore += 10;
    else if (taxCompliance === 'minor-issues') backgroundScore += 8;
    else if (taxCompliance === 'payment-plan') backgroundScore += 5;
    else backgroundScore += 0;
    
    // Travel history scoring
    const totalDaysAbsent = travelHistory.reduce((total, trip) => total + (trip.duration || 0), 0);
    const tripsOver6Months = travelHistory.filter(trip => trip.duration > 180);
    const tripsOver1Year = travelHistory.filter(trip => trip.duration > 365);
    
    // Physical presence calculation
    const fiveYears = 5 * 365;
    const physicalPresenceDays = fiveYears - totalDaysAbsent;
    const requiredDays = Math.floor(fiveYears / 2);
    const meetsPhysicalPresence = physicalPresenceDays >= requiredDays;
    
    if (meetsPhysicalPresence) travelScore += 10;
    if (tripsOver1Year.length === 0) travelScore += 5;
    if (tripsOver6Months.length === 0) travelScore += 5;
    
    // Generate issues
    if (!meetsPhysicalPresence) {
      issues.push('May not meet physical presence requirement');
    }
    if (tripsOver1Year.length > 0) {
      issues.push('Trips over 1 year break continuous residence');
    }
    if (tripsOver6Months.length > 0) {
      issues.push('Extended trips may affect continuous residence');
    }
    if (criminalHistory !== 'none' && criminalHistory !== 'minor-traffic') {
      issues.push('Criminal history requires careful review');
    }
    if (taxCompliance !== 'current') {
      issues.push('Tax compliance issues need resolution');
    }
    if (documents.length < 3) {
      issues.push('Limited documentation provided');
    }
    
    // Generate strengths
    if (meetsPhysicalPresence) {
      strengths.push('Meets physical presence requirement');
    }
    if (tripsOver6Months.length === 0) {
      strengths.push('No extended travel outside US');
    }
    if (criminalHistory === 'none') {
      strengths.push('Clean criminal background');
    }
    if (taxCompliance === 'current') {
      strengths.push('Current with tax obligations');
    }
    if (backgroundDetails.militaryService) {
      strengths.push('U.S. military service');
    }
    if (documents.length >= 5) {
      strengths.push('Comprehensive documentation');
    }
    
    const finalScore = Math.min(100, Math.max(0, applicationScore + eligibilityScore + documentScore + backgroundScore + travelScore));
    
    // Generate recommendations
    const recommendations = [];
    if (!meetsPhysicalPresence) {
      recommendations.push('Wait until you meet physical presence requirement');
    }
    if (tripsOver1Year.length > 0) {
      recommendations.push('Consult attorney about continuous residence breaks');
    }
    if (criminalHistory !== 'none' && criminalHistory !== 'minor-traffic') {
      recommendations.push('Obtain certified court records for all arrests/convictions');
    }
    if (taxCompliance !== 'current') {
      recommendations.push('Resolve all tax issues before applying');
    }
    if (documents.length < 5) {
      recommendations.push('Gather additional supporting documentation');
    }
    
    return {
      overall_score: finalScore,
      eligibility_score: eligibilityScore,
      eligibility_assessment: eligibilityScore >= 20 ? 'Strong' : eligibilityScore >= 15 ? 'Good' : 'Needs Review',
      document_analysis: {
        total_documents: documents.length,
        required_found: foundTypes.length,
        completeness: foundTypes.length >= 3 ? 'Good' : foundTypes.length >= 2 ? 'Partial' : 'Insufficient',
        score: documentScore
      },
      background_analysis: {
        criminal_status: criminalHistory,
        tax_status: taxCompliance,
        moral_character: backgroundScore >= 15 ? 'Good' : backgroundScore >= 10 ? 'Acceptable' : 'Concerning',
        score: backgroundScore
      },
      travel_analysis: {
        total_days_absent: totalDaysAbsent,
        physical_presence_days: physicalPresenceDays,
        meets_physical_presence: meetsPhysicalPresence,
        trips_over_6_months: tripsOver6Months.length,
        trips_over_1_year: tripsOver1Year.length,
        continuous_residence: tripsOver1Year.length === 0 ? 'Maintained' : 'Broken',
        score: travelScore
      },
      strengths: strengths.length > 0 ? strengths : ['Basic application information provided'],
      risks: issues.length > 0 ? issues : ['No major issues identified'],
      recommendations: recommendations.length > 0 ? recommendations : ['Application appears ready for submission']
    };
  };

  React.useEffect(() => {
    if (Object.keys(analysisResults).length === 0) {
      performRealAnalysis();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🔍 Background Check & Complication Review</h3>
        <p className="text-gray-400 text-lg">AI-powered analysis of your naturalization eligibility</p>
      </div>

      {analyzing && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Naturalization Case</h4>
          <p className="text-gray-400">Evaluating eligibility, background, travel history, and documentation...</p>
        </div>
      )}

      {!analyzing && Object.keys(analysisResults).length > 0 && (
        <>
          {/* Overall Assessment */}
          <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
            <div className="text-center">
              <div className="text-6xl font-bold text-blue-400 mb-2">{analysisResults.overall_score}%</div>
              <div className="text-xl text-white mb-4">Naturalization Application Strength</div>
              <div className={`text-lg ${
                analysisResults.overall_score >= 80 ? 'text-emerald-400' :
                analysisResults.overall_score >= 60 ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {analysisResults.overall_score >= 80 ? 'Strong Case - Ready to Apply' :
                 analysisResults.overall_score >= 60 ? 'Good Case - Minor Issues to Address' :
                 'Needs Improvement - Major Issues to Resolve'}
              </div>
            </div>
          </div>

          {/* Detailed Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📋 Eligibility Assessment</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Pathway:</span>
                  <span className="text-blue-400 font-medium">{formData.pathDetails?.title || 'Not Selected'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Assessment:</span>
                  <span className={`font-medium ${
                    analysisResults.eligibility_assessment === 'Strong' ? 'text-emerald-400' :
                    analysisResults.eligibility_assessment === 'Good' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.eligibility_assessment}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Years in US:</span>
                  <span className="text-blue-400 font-medium">{formData.residenceDetails?.yearsInUS || 'Not Specified'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Eligibility Score:</span>
                  <span className="text-blue-400 font-medium">{analysisResults.eligibility_score || 0}/25</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📄 Document Analysis</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Total Documents:</span>
                  <span className="text-blue-400 font-medium">{analysisResults.document_analysis?.total_documents}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Required Found:</span>
                  <span className="text-blue-400 font-medium">{analysisResults.document_analysis?.required_found}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Completeness:</span>
                  <span className={`font-medium ${
                    analysisResults.document_analysis?.completeness === 'Good' ? 'text-emerald-400' :
                    analysisResults.document_analysis?.completeness === 'Partial' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.document_analysis?.completeness}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Document Score:</span>
                  <span className="text-blue-400 font-medium">{Math.round(analysisResults.document_analysis?.score || 0)}/20</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">⚖️ Background Analysis</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Criminal Status:</span>
                  <span className={`font-medium ${
                    analysisResults.background_analysis?.criminal_status === 'none' ? 'text-emerald-400' :
                    analysisResults.background_analysis?.criminal_status === 'minor-traffic' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.background_analysis?.criminal_status || 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Tax Status:</span>
                  <span className={`font-medium ${
                    analysisResults.background_analysis?.tax_status === 'current' ? 'text-emerald-400' :
                    'text-yellow-400'
                  }`}>
                    {analysisResults.background_analysis?.tax_status || 'Current'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Moral Character:</span>
                  <span className={`font-medium ${
                    analysisResults.background_analysis?.moral_character === 'Good' ? 'text-emerald-400' :
                    analysisResults.background_analysis?.moral_character === 'Acceptable' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.background_analysis?.moral_character}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Background Score:</span>
                  <span className="text-blue-400 font-medium">{Math.round(analysisResults.background_analysis?.score || 0)}/20</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">✈️ Travel Analysis</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Days Absent:</span>
                  <span className="text-blue-400 font-medium">{analysisResults.travel_analysis?.total_days_absent || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Physical Presence Days:</span>
                  <span className={`font-medium ${
                    analysisResults.travel_analysis?.meets_physical_presence ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {analysisResults.travel_analysis?.physical_presence_days || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Trips Over 6 Months:</span>
                  <span className={`font-medium ${
                    (analysisResults.travel_analysis?.trips_over_6_months || 0) === 0 ? 'text-emerald-400' : 'text-yellow-400'
                  }`}>
                    {analysisResults.travel_analysis?.trips_over_6_months || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Continuous Residence:</span>
                  <span className={`font-medium ${
                    analysisResults.travel_analysis?.continuous_residence === 'Maintained' ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {analysisResults.travel_analysis?.continuous_residence}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Travel Score:</span>
                  <span className="text-blue-400 font-medium">{Math.round(analysisResults.travel_analysis?.score || 0)}/20</span>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Metrics Overview */}
          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 p-6 rounded-lg border border-blue-400/30">
            <h4 className="text-blue-300 font-semibold mb-4">📊 Detailed Case Metrics</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400 mb-1">
                  {analysisResults.travel_analysis?.physical_presence_days || 0}/{Math.floor(5 * 365 / 2)}
                </div>
                <div className="text-gray-300 text-sm">Physical Presence Days</div>
                <div className={`text-xs mt-1 ${
                  (analysisResults.travel_analysis?.physical_presence_days || 0) >= Math.floor(5 * 365 / 2) ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(analysisResults.travel_analysis?.physical_presence_days || 0) >= Math.floor(5 * 365 / 2) ? 'Meets Requirement' : 'Below Requirement'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400 mb-1">
                  {Math.round(((analysisResults.document_analysis?.required_found || 0) / Math.max(1, (analysisResults.document_analysis?.total_documents || 1))) * 100)}%
                </div>
                <div className="text-gray-300 text-sm">Document Match Rate</div>
                <div className={`text-xs mt-1 ${
                  ((analysisResults.document_analysis?.required_found || 0) / Math.max(1, (analysisResults.document_analysis?.total_documents || 1))) >= 0.75 ? 'text-emerald-400' : 'text-yellow-400'
                }`}>
                  Required docs found
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400 mb-1">
                  {Math.round(
                    ((analysisResults.eligibility_score || 0) + 
                     (analysisResults.document_analysis?.score || 0) + 
                     (analysisResults.background_analysis?.score || 0) + 
                     (analysisResults.travel_analysis?.score || 0)) / 85 * 100
                  )}%
                </div>
                <div className="text-gray-300 text-sm">Component Score Average</div>
                <div className={`text-xs mt-1 ${
                  analysisResults.overall_score >= 80 ? 'text-emerald-400' :
                  analysisResults.overall_score >= 60 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  Weighted calculation
                </div>
              </div>
            </div>
          </div>

          {/* Strengths and Risks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
              <h4 className="text-emerald-300 font-semibold mb-4">✅ Application Strengths</h4>
              <ul className="space-y-2">
                {analysisResults.strengths?.map((strength, index) => (
                  <li key={index} className="text-emerald-200 text-sm flex items-start">
                    <span className="text-emerald-400 mr-2 mt-1">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
              <h4 className="text-red-300 font-semibold mb-4">⚠️ Potential Issues</h4>
              <ul className="space-y-2">
                {analysisResults.risks?.map((risk, index) => (
                  <li key={index} className="text-red-200 text-sm flex items-start">
                    <span className="text-red-400 mr-2 mt-1">•</span>
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
            <h4 className="text-yellow-300 font-semibold mb-4">💡 Recommendations</h4>
            <ul className="space-y-2">
              {analysisResults.recommendations?.map((rec, index) => (
                <li key={index} className="text-yellow-200 text-sm flex items-start">
                  <span className="text-yellow-400 mr-2 mt-1">{index + 1}.</span>
                  {typeof rec === 'string' ? rec : (
                    <div>
                      <div className="font-medium text-yellow-300 mb-1">{rec.category}</div>
                      {rec.items?.map((item, itemIndex) => (
                        <div key={itemIndex} className="ml-2 mb-1">• {item}</div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Travel History
        </button>
        <button
          onClick={onNext}
          disabled={analyzing}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !analyzing
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Document Enhancement →
        </button>
      </div>
    </div>
  );
};